import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../redux/store';
import CircularProgress from '@mui/material/CircularProgress';
import LoadingCenter from './util/LoadingCenter';
import Box from '@mui/material/Box';
import { useLocation } from 'react-router-dom';
import { t } from 'i18next';
import { Category } from './docsComponents/CategoryTreeDefault';
import Modal from '@mui/material/Modal';
import AddDocs from './modal/AddDocs';
import EditDocs from './modal/EditDocs';
import PolicyView from './docsComponents/PolicyView';
import DocsTable from './docsComponents/DocsTable';
import ContractsTab from './docsComponents/ContractsTab';
import TabMenu from './util/TabMenu';
import PageToolbar from './util/PageToolbar';
import './docsComponents/documents.css';

interface DocsProps {
  access: any,
  baseUrl: any
}

const Documents: React.FC<DocsProps> = ({ access, baseUrl }) => {
  const [showCategory, setShowCategory] = useState(false);
  const [showDocument, setShowDocument] = useState(false);
  const [category, setCategory] = useState<Category[]>([]);
  const [docs, setDocs] = useState([]);
  const [track, setTrack] = useState('');
  const [isCategoryListVisible, setCategoryListVisible] = useState(false);
  const [categoryName, setCategoryName] = useState('');
  const [active, setActive] = useState(true);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number[]>([]);
  const [selectedCategoryIdCheck, setSelectedCategoryIdCheck] = useState<number[]>([]);
  const [selectedCategoryIdDefault, setSelectedCategoryIdDefault] = useState<number[]>([]);
  const [none, setNone] = useState(false);
  const [value, setValue] = useState('policies');
  const [sidebarCat, setSidebarCat] = useState<number | null>(null);
  const [addingCat, setAddingCat] = useState(false);
  const [newCatName, setNewCatName] = useState('');
  const newCatRef = useRef<HTMLInputElement>(null);
  const [renamingCatId, setRenamingCatId] = useState<number | null>(null);
  const [renameCatName, setRenameCatName] = useState('');
  const renameCatRef = useRef<HTMLInputElement>(null);
  const [panelDoc, setPanelDoc] = useState<any>(null);
  const [panelEditDocId, setPanelEditDocId] = useState<any>(null);
  const [showPanelPolicy, setShowPanelPolicy] = useState(false);
  const [showReminder, setShowReminder] = useState(false);
  const [sendingReminder, setSendingReminder] = useState(false);
  // Variant 5 toolbar state
  const [viewMode, setViewMode] = useState<'list' | 'cards'>('list');
  const [catFilter, setCatFilter] = useState<number[]>([]);
  const auth = useSelector((state: RootState) => state.auth.value);
  const location = useLocation();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const { data } = await axios.get('user');
        const { data: Categorys } = await axios.get('document-category');
        if (data) {
        }
        setCategory(Categorys);
      } catch (e) {
        // auth errors are handled by the global interceptor
      }
    };
    checkAuth();
  }, [dispatch, location.pathname]);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const buildPath = (id: any) => {
          const current = category.find(cat => cat.id === id);
          if (!current) return '';
          const parent = category.find(cat => cat.id === current.parentId);
          return parent ? `\\${parent.name}\\${current.name}` : `\\${current.name}`;
        };
        if (selectedCategoryIdDefault.length > 0) {
          const result = buildPath(selectedCategoryIdDefault[0]);
          setTrack(result);
          const { data } = await axios.get(`document/category/${selectedCategoryIdDefault[0]}`);
          setDocs(data);
        } else {
          setTrack('\\');
          const { data } = await axios.get('document');
          setDocs(data);
        }
      } catch (e) {
        console.error('Error', e);
      }
    };

    checkAuth();
  }, [selectedCategoryIdDefault, category]);

  const refreshCategoryDocs = async () => {
    try {
      const buildPath = (id: any) => {
        const current = category.find(cat => cat.id === id);
        if (!current) return '';
        const parent = category.find(cat => cat.id === current.parentId);
        return parent ? `\\${parent.name}\\${current.name}` : `\\${current.name}`;
      };
      const { data } = await axios.get('document-category');
      if (data) {
        setCategory(data);
      }
      if (selectedCategoryIdDefault.length > 0) {
        const result = buildPath(selectedCategoryIdDefault[0]);
        setTrack(result);
        const { data } = await axios.get(`document/category/${selectedCategoryIdDefault[0]}`);
        setDocs(data);
      } else {
        setTrack('\\');
        const { data } = await axios.get('document');
        setDocs(data);
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
    }
  };

  const handleShowCategoryCreate = () => {
    setShowCategory(true);
    setCategoryListVisible(false);
    setSelectedCategoryId([-1]);
    setCategoryName('none');
  };

  //show category
  const handleClickCategoryBox = (event: React.MouseEvent) => {
    setCategoryListVisible(!isCategoryListVisible);
    if (isCategoryListVisible || selectedCategoryId.length > 0) {
      setSelectedCategoryId(selectedCategoryIdCheck)
    } else {
      setSelectedCategoryId([]);
    }
    event.stopPropagation();
  };

  const handleCategoryListClick = (event: React.MouseEvent) => {
    event.stopPropagation();
  };

  const handleClickAddCategoryId = (selectedId: number) => {
    const selectedCategory = category?.find(node => node.id === selectedId);
    if (selectedCategory) {
      setCategoryName(selectedCategory.name)
      setCategoryListVisible(false);
      setSelectedCategoryIdCheck([selectedId])
    } else {
      setCategoryName('none')
      console.log('Category not found');
      setCategoryListVisible(false);
    }
  };

  const handleNodeSelect = (nodeId: number) => {
    setSelectedCategoryId([nodeId])
  };

  const handleClose = () => {
    setShowCategory(false);
    setCategoryListVisible(false);
    setSelectedCategoryId([]);
    setCategoryName('');
    setSelectedCategoryIdCheck([]);
  }

  const handleCloseAll = () => {
    setShowDocument(false);
    setSelectedCategoryId([]);
    setActive(true);
    setCategoryName('');
    setSelectedCategoryIdCheck([]);
  }

  const catCount = (id: number) => docs.filter((d: any) => d.documentCategoryId === id).length;

  // stats for the right management panel
  const manageStats = useMemo(() => {
    const allLearn: any[] = (docs as any[]).flatMap((d: any) => d.document_learn || []);
    const learned = allLearn.filter(e => e.status === 'learned').length;
    const pending = allLearn.filter(e => e.status === 'new').length;
    const total = learned + pending;
    const pct = total ? Math.round((learned / total) * 100) : 0;
    // unique users with any pending acknowledgement
    const pendingUsers: { id: number; first_name: string; last_name: string }[] = [];
    const seen = new Set<number>();
    for (const e of allLearn) {
      if (e.status === 'new' && e.user && !seen.has(e.user.id)) {
        seen.add(e.user.id);
        pendingUsers.push(e.user);
      }
    }
    return { learned, pending, total, pct, pendingUsers };
  }, [docs]);

  const fileKindLabel = (url: string) => {
    const ext = (url || '').split('.').pop()?.toLowerCase();
    if (ext === 'doc' || ext === 'docx') return { label: 'DOC', cls: 'doc' };
    if (ext === 'xls' || ext === 'xlsx' || ext === 'csv') return { label: 'XLS', cls: 'xls' };
    return { label: 'PDF', cls: '' };
  };

  const docCatName = (id: any) => (category || []).find((c: any) => c.id === id)?.name || '';

  const panelDocStats = useMemo(() => {
    if (!panelDoc) return null;
    const learn: any[] = panelDoc.document_learn || [];
    const learned = learn.filter((e: any) => e.status === 'learned');
    const pending = learn.filter((e: any) => e.status === 'new');
    const total = learned.length + pending.length;
    const pct = total ? Math.round((learned.length / total) * 100) : 0;
    return { learned, pending, total, pct };
  }, [panelDoc]);

  // Keep panelDoc in sync when docs refresh
  useEffect(() => {
    if (panelDoc) {
      const updated = (docs as any[]).find((d: any) => d.id === panelDoc.id);
      if (updated) setPanelDoc(updated);
    }
  }, [docs]); // eslint-disable-line

  const handleOpenPanelPolicy = (doc: any) => {
    setShowPanelPolicy(true);
  };
  const handleClosePanelPolicy = () => setShowPanelPolicy(false);
  const handleClosePanelEdit = () => { setPanelEditDocId(null); setNone(false); };

  const catStats = useMemo(() => {
    return (category as any[]).map((cat: any, idx: number) => {
      const catDocs = (docs as any[]).filter((d: any) => d.documentCategoryId === cat.id);
      const catLearn: any[] = catDocs.flatMap((d: any) => d.document_learn || []);
      const catLearned = catLearn.filter(e => e.status === 'learned').length;
      const catPending = catLearn.filter(e => e.status === 'new').length;
      const catTotal = catLearn.length;
      const catPct = catTotal ? Math.round((catLearned / catTotal) * 100) : 0;
      return { id: cat.id, name: cat.name, pct: catPct, docCount: catDocs.length, catLearned, catPending, catTotal, idx };
    });
  }, [docs, category]);

  const handleSendReminder = async () => {
    if (!panelDoc) return;
    setSendingReminder(true);
    try {
      await axios.post(`document/send-reminder/${panelDoc.id}`);
      enqueueSnackbar(t('TrainingRemindSent'), { variant: 'success' });
    } catch {
      enqueueSnackbar(t('Error'), { variant: 'error' });
    } finally {
      setSendingReminder(false);
      setShowReminder(false);
    }
  };

  const CAT_COLORS = [
    'rgba(244,63,94,.1)', 'rgba(8,145,178,.1)', 'rgba(139,92,246,.1)',
    'rgba(234,179,8,.1)', 'rgba(34,197,94,.1)', 'rgba(96,165,250,.1)',
    'rgba(249,115,22,.1)', 'rgba(20,184,166,.1)',
  ];
  const catBg = (idx: number) => CAT_COLORS[idx % CAT_COLORS.length];

  const handleSidebarCat = (id: number | null) => {
    setSidebarCat(id);
    setCatFilter(id !== null ? [id] : []);
  };

  const startAddCat = () => {
    setAddingCat(true);
    setNewCatName('');
    setTimeout(() => newCatRef.current?.focus(), 50);
  };

  const cancelAddCat = () => {
    setAddingCat(false);
    setNewCatName('');
  };

  const confirmAddCat = useCallback(async () => {
    const name = newCatName.trim();
    if (!name) { cancelAddCat(); return; }
    try {
      await axios.post('document-category', { name });
      await refreshCategoryDocs();
    } catch { /* ignore */ }
    cancelAddCat();
  }, [newCatName]); // eslint-disable-line

  const startRenameCat = (cat: Category, e: React.MouseEvent) => {
    e.stopPropagation();
    setRenamingCatId(cat.id);
    setRenameCatName(cat.name);
    setTimeout(() => renameCatRef.current?.select(), 50);
  };

  const cancelRenameCat = () => {
    setRenamingCatId(null);
    setRenameCatName('');
  };

  const confirmRenameCat = useCallback(async () => {
    const name = renameCatName.trim();
    if (!name || renamingCatId === null) { cancelRenameCat(); return; }
    try {
      await axios.put(`document-category/${renamingCatId}`, { name });
      await refreshCategoryDocs();
    } catch { /* ignore */ }
    cancelRenameCat();
  }, [renameCatName, renamingCatId]); // eslint-disable-line

  return (
    <div className="docs-redesign section-page">

      {/* ── Tabs + content inside TabMenu ── */}
      <TabMenu
        value={value}
        onChange={(v: string) => setValue(v)}
        tabs={[
          { label: `${t('Policies')} (${docs?.length ?? 0})`, value: 'policies' },
          { label: t('Contracts'), value: 'contracts' },
        ]}
      >

      {/* ════════════ POLICIES ════════════ */}
      {value === 'policies' && (
        auth ? (
          <>
            {/* ── Page toolbar ── */}
            <PageToolbar>
              <div style={{ flex: 1 }} />
              <div style={{ display: 'flex', gap: 3 }}>
                <button type="button" className={`dt-btn-ghost${viewMode === 'list' ? ' active' : ''}`} onClick={() => setViewMode('list')}>
                  ≣ {t('ListView')}
                </button>
                <button type="button" className={`dt-btn-ghost${viewMode === 'cards' ? ' active' : ''}`} onClick={() => setViewMode('cards')}>
                  ▦ {t('CardsView')}
                </button>
              </div>
            </PageToolbar>

            {/* ── Sidebar + Content layout ── */}
            <div className="dr-content-layout">

              {/* Sidebar */}
              <div className="dr-sidebar dt-card-wrap">
                <div className="dr-sidebar-head">
                  <span className="dr-sidebar-title">{t('DiskFolders')}</span>
                  {access.document_add && (
                    <button type="button" className="dr-btn-mini" onClick={startAddCat} title={t('Add category') as string}>+</button>
                  )}
                </div>
                <div className="dr-sidebar-scroll">
                  <div
                    className={`dr-sidebar-item${sidebarCat === null ? ' active' : ''}`}
                    onClick={() => handleSidebarCat(null)}>
                    <span className="dr-sidebar-ico" style={{ background: 'rgba(8,145,178,.12)' }}>📂</span>
                    <span className="dr-sidebar-label">{t('DiskAllFiles')}</span>
                    <span className="dr-sidebar-n">{docs.length}</span>
                  </div>

                  {/* Inline new category input */}
                  {addingCat && (
                    <div className="dr-sidebar-item dr-sidebar-adding">
                      <span className="dr-sidebar-ico" style={{ background: 'rgba(37,99,235,.1)' }}>📁</span>
                      <input
                        ref={newCatRef}
                        className="dr-cat-input"
                        value={newCatName}
                        onChange={e => setNewCatName(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') confirmAddCat();
                          if (e.key === 'Escape') cancelAddCat();
                        }}
                        onBlur={confirmAddCat}
                        placeholder={t('Categories') as string}
                      />
                    </div>
                  )}

                  {category.length > 0 && (
                    <>
                      <div className="dr-sidebar-sec">{t('Categories')}</div>
                      {category.map((cat: any, idx: number) => (
                        <div
                          key={cat.id}
                          className={`dr-sidebar-item${sidebarCat === cat.id ? ' active' : ''}`}
                          onClick={() => handleSidebarCat(cat.id)}
                          onDoubleClick={access.document_add ? (e) => startRenameCat(cat, e) : undefined}>
                          <span className="dr-sidebar-ico" style={{ background: catBg(idx) }}>📁</span>
                          {renamingCatId === cat.id ? (
                            <input
                              ref={renameCatRef}
                              className="dr-cat-input"
                              value={renameCatName}
                              onChange={e => setRenameCatName(e.target.value)}
                              onKeyDown={e => {
                                if (e.key === 'Enter') confirmRenameCat();
                                if (e.key === 'Escape') cancelRenameCat();
                              }}
                              onBlur={confirmRenameCat}
                              onClick={e => e.stopPropagation()}
                            />
                          ) : (
                            <span className="dr-sidebar-label">{cat.name}</span>
                          )}
                          <span className="dr-sidebar-n">{catCount(cat.id)}</span>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              </div>

              {/* Main docs card — own scroll */}
              <div className="dt-card-wrap" style={{ flex: 1, minWidth: 0, overflowY: 'auto', minHeight: 0 }}>
                <DocsTable docs={docs}
                  baseUrl={baseUrl}
                  refreshCategoryDocs={refreshCategoryDocs}
                  access={access}
                  viewMode={viewMode}
                  catFilter={catFilter}
                  category={category}
                  selectedCategoryId={selectedCategoryId}
                  handleClickCategoryBox={handleClickCategoryBox}
                  categoryName={categoryName}
                  isCategoryListVisible={isCategoryListVisible}
                  handleCategoryListClick={handleCategoryListClick}
                  handleNodeSelect={handleNodeSelect}
                  handleClickAddCategoryId={handleClickAddCategoryId}
                  setSelectedCategoryId={setSelectedCategoryId}
                  setCategoryName={setCategoryName}
                  none={none}
                  setNone={setNone}
                  panelDoc={panelDoc}
                  setPanelDoc={setPanelDoc}
                  onOpenPolicy={handleOpenPanelPolicy} />
              </div>

              {/* ── Right management panel — document_add only ── */}
              {access?.document_add && (
                <div className="dr-manage-panel dt-card-wrap">
                  <div className="dr-sidebar-head">
                    {panelDoc ? (
                      <button type="button" className="dr-back-btn" onClick={() => setPanelDoc(null)}>
                        ← {t('Management')}
                      </button>
                    ) : (
                      <span className="section-label">{t('Management')}</span>
                    )}
                  </div>

                  {panelDoc ? (
                    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
                      {/* Doc info */}
                      <div className="dr-panel-docinfo">
                        <div className={`dr-doc-ico ${fileKindLabel(panelDoc.file_url).cls}`}
                          style={{ width: 34, height: 34, fontSize: 10, borderRadius: 9, flexShrink: 0 }}>
                          {fileKindLabel(panelDoc.file_url).label}
                        </div>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          {docCatName(panelDoc.documentCategoryId) && (
                            <div className="dr-panel-catname">{docCatName(panelDoc.documentCategoryId)}</div>
                          )}
                          <div className="dr-panel-title">{panelDoc.title}</div>
                          {panelDoc.description && (
                            <div className="dr-panel-desc">{panelDoc.description}</div>
                          )}
                        </div>
                      </div>

                      {/* KPI blocks: acknowledged + pending counts */}
                      {panelDocStats && panelDocStats.total > 0 && (
                        <div className="dr-doc-kpi">
                          <div className="dr-dk green">
                            <div className="dr-dk-n">{panelDocStats.learned.length}</div>
                            <div className="dr-dk-l">{t('PolicyStatusAcknowledged')}</div>
                          </div>
                          <div className="dr-dk amber">
                            <div className="dr-dk-n">{panelDocStats.pending.length}</div>
                            <div className="dr-dk-l">{t('PolicyStatusPending')}</div>
                          </div>
                        </div>
                      )}

                      {/* Sign-off progress for this doc */}
                      {panelDocStats && panelDocStats.total > 0 && (
                        <div className="dr-panel-progress">
                          <div className="dr-panel-pct-row">
                            <span>{t('SignedOff')}</span>
                            <span style={{ color: '#059669', fontWeight: 800 }}>{panelDocStats.pct}%</span>
                          </div>
                          <div className="dr-manage-progress-bar">
                            <span style={{ width: `${panelDocStats.pct}%` }} />
                          </div>
                          <div className="dr-panel-counts">
                            <span style={{ color: '#059669' }}>✓ {panelDocStats.learned.length}</span>
                            <span style={{ color: '#d97706' }}>⏳ {panelDocStats.pending.length}</span>
                          </div>
                        </div>
                      )}

                      {/* User list — acknowledged + pending in one view */}
                      <div className="dr-panel-users">
                        {panelDocStats && panelDocStats.learned.length > 0 && (
                          <>
                            <div className="dr-panel-section-label">
                              {t('PolicyStatusAcknowledged')} ({panelDocStats.learned.length})
                            </div>
                            {panelDocStats.learned.map((e: any) => (
                              <div key={e.id} className="dr-panel-user-row">
                                <div className="dr-manage-av">
                                  {(e.user?.first_name?.[0] || '') + (e.user?.last_name?.[0] || '')}
                                </div>
                                <div className="dr-panel-username">{e.user?.first_name} {e.user?.last_name}</div>
                                <div className="dr-panel-userbadge ok">✓</div>
                              </div>
                            ))}
                          </>
                        )}
                        {panelDocStats && panelDocStats.pending.length > 0 && (
                          <>
                            <div className="dr-panel-section-label"
                              style={{ marginTop: panelDocStats.learned.length > 0 ? 8 : 0 }}>
                              {t('PolicyStatusPending')} ({panelDocStats.pending.length})
                            </div>
                            {panelDocStats.pending.map((e: any) => (
                              <div key={e.id} className="dr-panel-user-row">
                                <div className="dr-manage-av"
                                  style={{ background: 'linear-gradient(135deg,#d97706,#fbbf24)' }}>
                                  {(e.user?.first_name?.[0] || '') + (e.user?.last_name?.[0] || '')}
                                </div>
                                <div className="dr-panel-username">{e.user?.first_name} {e.user?.last_name}</div>
                                <div className="dr-panel-userbadge pnd">⏳</div>
                              </div>
                            ))}
                          </>
                        )}
                        {(!panelDocStats || panelDocStats.total === 0) && (
                          <div className="dr-panel-empty">{t('PolicyStatusNotAssigned')}</div>
                        )}
                      </div>

                      {/* Bottom actions */}
                      <div className="dr-panel-actions">
                        <button type="button" className="dr-panel-open-btn"
                          onClick={() => handleOpenPanelPolicy(panelDoc)}>
                          📖 {t('OpenToReadSign').split(' ')[0]}
                        </button>
                        {panelDocStats && panelDocStats.pending.length > 0 && (
                          <button type="button" className="dr-reminder-btn"
                            onClick={() => setShowReminder(true)}
                            disabled={sendingReminder}>
                            📧 {t('SendReminder')} ({panelDocStats.pending.length})
                          </button>
                        )}
                        <button type="button" className="dr-panel-edit-btn"
                          onClick={() => { setPanelEditDocId(String(panelDoc.id)); setNone(true); }}>
                          ✏️ {t('Edit')}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="dr-manage-body">
                      {/* Add document */}
                      <button type="button" className="dr-manage-add-btn" onClick={() => setShowDocument(true)}>
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0 }}>
                          <path d="M7 1v12M1 7h12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                        Add new
                      </button>

                      {/* Donut chart */}
                      {manageStats.total > 0 && (
                        <div className="dr-donut-wrap">
                          <div style={{ position: 'relative', width: 88, height: 88 }}>
                            <svg className="dr-donut-svg" width="88" height="88" viewBox="0 0 88 88">
                              <circle cx="44" cy="44" r="36" fill="none" stroke="var(--border-input)" strokeWidth="9" />
                              <circle cx="44" cy="44" r="36" fill="none" stroke="#059669" strokeWidth="9"
                                strokeDasharray={`${2 * Math.PI * 36 * manageStats.pct / 100} ${2 * Math.PI * 36}`}
                                strokeLinecap="round" />
                            </svg>
                            <div className="dr-donut-center">
                              <div className="dr-donut-label">{manageStats.pct}%</div>
                              <div className="dr-donut-sublabel">{t('SignedOff')}</div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* 2×2 KPI grid */}
                      <div className="dr-manage-stats">
                        <div className="dr-stat">
                          <span className="dr-stat-n">{docs.length}</span>
                          <span className="dr-stat-l">{t('Total')}</span>
                        </div>
                        <div className="dr-stat">
                          <span className="dr-stat-n">{category.length}</span>
                          <span className="dr-stat-l">{t('Categories')}</span>
                        </div>
                        {manageStats.total > 0 && (
                          <>
                            <div className="dr-stat green">
                              <span className="dr-stat-n">{manageStats.learned}</span>
                              <span className="dr-stat-l">{t('PolicyStatusAcknowledged')}</span>
                            </div>
                            <div className="dr-stat amber">
                              <span className="dr-stat-n">{manageStats.pending}</span>
                              <span className="dr-stat-l">{t('PolicyStatusPending')}</span>
                            </div>
                          </>
                        )}
                      </div>

                      {/* Category info list */}
                      {catStats.filter(c => c.docCount > 0).length > 0 && (
                        <div className="dr-catinfo-list">
                          <div className="dr-panel-section-label" style={{ paddingTop: 2 }}>{t('Categories')}</div>
                          {catStats.filter(c => c.docCount > 0).map(cat => (
                            <div key={cat.id}
                              className={`dr-catinfo-row${sidebarCat === cat.id ? ' active' : ''}`}
                              onClick={() => handleSidebarCat(cat.id)}>
                              <span className="dr-sidebar-ico" style={{ background: catBg(cat.idx), flexShrink: 0, fontSize: 14 }}>📁</span>
                              <div className="dr-catinfo-content">
                                <div className="dr-catinfo-name">{cat.name}</div>
                                <div className="dr-catinfo-stats">
                                  <span style={{ color: 'var(--text-color-calendar-week)' }}>{cat.docCount} {t('Doc')}</span>
                                  {cat.catTotal > 0 && (
                                    <>
                                      <span style={{ color: '#059669' }}>✓ {cat.catLearned}</span>
                                      <span style={{ color: '#d97706' }}>⏳ {cat.catPending}</span>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        ) : (
          <LoadingCenter />
        )
      )}

      {/* ════════════ CONTRACTS ════════════ */}
      {value === 'contracts' && (
        <ContractsTab access={access} baseUrl={baseUrl} />
      )}

      </TabMenu>

      {/* Edit modal triggered from right panel */}
      {panelEditDocId && (
        <Modal
          open={!!panelEditDocId}
          onClose={handleClosePanelEdit}
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: '18px' }}>
          <>
            <EditDocs
              category={category}
              selectedCategoryId={selectedCategoryId}
              handleClickCategoryBox={handleClickCategoryBox}
              categoryName={categoryName}
              isCategoryListVisible={isCategoryListVisible}
              handleCategoryListClick={handleCategoryListClick}
              handleNodeSelect={handleNodeSelect}
              handleClickAddCategoryId={handleClickAddCategoryId}
              refreshCategoryDocs={refreshCategoryDocs}
              documentId={panelEditDocId}
              setSelectedCategoryId={setSelectedCategoryId}
              setCategoryName={setCategoryName}
              none={none}
              handleCloseEdit={handleClosePanelEdit}
              baseUrl={baseUrl}
            />
          </>
        </Modal>
      )}

      {/* PolicyView triggered from right panel Open button */}
      <Modal
        open={showPanelPolicy}
        onClose={handleClosePanelPolicy}
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: '18px' }}>
        <>
          {showPanelPolicy && panelDoc && (
            <PolicyView
              doc={panelDoc}
              baseUrl={baseUrl}
              access={access}
              category={category}
              refreshCategoryDocs={refreshCategoryDocs}
              handleClosePolicy={handleClosePanelPolicy}
            />
          )}
        </>
      </Modal>

      <Modal
        open={showDocument}
        onClose={() => handleCloseAll()}
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: '18px' }}>
        <>
          {showDocument &&
            <AddDocs
              category={category}
              selectedCategoryId={selectedCategoryId}
              handleClickCategoryBox={handleClickCategoryBox}
              categoryName={categoryName}
              isCategoryListVisible={isCategoryListVisible}
              handleCategoryListClick={handleCategoryListClick}
              handleNodeSelect={handleNodeSelect}
              handleClickAddCategoryId={handleClickAddCategoryId}
              refreshCategoryDocs={refreshCategoryDocs}
              active={active}
              setActive={setActive}
              handleCloseAll={handleCloseAll}
              none={none}
            />}
        </>
      </Modal>

      {/* Reminder confirmation dialog */}
      {showReminder && panelDocStats && (
        <div className="dr-confirm-overlay" onClick={() => setShowReminder(false)}>
          <div className="dr-confirm-box" onClick={e => e.stopPropagation()}>
            <div className="dr-confirm-title">📧 {t('SendReminder')}</div>
            <div className="dr-confirm-text">
              {t('SendReminder')} will be sent to <strong>{panelDocStats.pending.length}</strong> people:
            </div>
            <div className="dr-confirm-names">
              {panelDocStats.pending.map((e: any) => (
                <div key={e.id} className="dr-confirm-name">{e.user?.first_name} {e.user?.last_name}</div>
              ))}
            </div>
            <div className="dr-confirm-btns">
              <button type="button" className="dr-confirm-cancel" onClick={() => setShowReminder(false)}>
                {t('Cancel')}
              </button>
              <button type="button" className="dr-confirm-send" onClick={handleSendReminder} disabled={sendingReminder}>
                {sendingReminder ? '...' : t('Send')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Documents;
